<template lang="pug">
  .webchat-ui-custom-configuration
    .settings-container
      .header-config-container
        .field-label
          span {{ $t(`${BASE_TRANSLATION_PATH}.input_box.section_title`) }}
        .sub-field
          label {{ $t(`${BASE_TRANSLATION_PATH}.input_box.branding_text`) }}
          .inline-label-box
            aiq-input(v-model="localWebchatUiCustomConfigStrings.input_box.branding_text"
              size="small")
        .sub-field
          label {{ $t(`${BASE_TRANSLATION_PATH}.input_box.placeholder_text`) }}
          .inline-label-box
            aiq-input(v-model="localWebchatUiCustomConfigStrings.input_box.placeholder_text"
              size="small")
        .sub-field
          label {{ $t(`${BASE_TRANSLATION_PATH}.input_box.text_limit`) }}
          .inline-label-box
            aiq-input(v-model.number="localWebchatUiCustomConfigs.input_box.text_limit"
              size="small"
              type="number"
              min="0")
        .sub-field
          label {{ $t(`${BASE_TRANSLATION_PATH}.input_box.upload_button_icon`) }}
          .inline-label-box(v-if="localWebchatUiCustomConfigs.input_box.upload_button_icon_url")
            aiq-image.icon-preview(
              :src="localWebchatUiCustomConfigs.input_box.upload_button_icon_url"
              :key="localWebchatUiCustomConfigs.input_box.upload_button_icon_url")
          .inline-label-box
            .upload-button
              aiq-upload.no-margin(
                v-model="localWebchatUiCustomConfigs.input_box.upload_button_icon_url"
                :http-request="upload()"
                :action="uploadUrl"
                accept="image/*"
                :headers="headers"
                :show-file-list="false"
                :disabled="!uploadEnabled"
                :on-success="(res) => onHandleUploadSuccess(res, ['input_box', 'upload_button_icon_url'])"
                ref="uploadButtonIconUploader")
                aiq-button.upload-btn(size="small" :disabled="!uploadEnabled") {{ $t(`global.upload`) }}
              aiq-button.delete-btn(
                v-if="localWebchatUiCustomConfigs.input_box.upload_button_icon_url"
                size="small"
                type="danger"
                @click="cleanUploadButtonIcon") {{ $t(`global.delete`) }}
        .sub-field
          label {{ $t(`${BASE_TRANSLATION_PATH}.input_box.send_button_icon`) }}
          .inline-label-box(v-if="localWebchatUiCustomConfigs.input_box.send_button_icon_url")
            aiq-image.icon-preview(
              :src="localWebchatUiCustomConfigs.input_box.send_button_icon_url"
              :key="localWebchatUiCustomConfigs.input_box.send_button_icon_url")
          .inline-label-box
            .upload-button
              aiq-upload.no-margin(
                v-model="localWebchatUiCustomConfigs.input_box.send_button_icon_url"
                :http-request="upload()"
                :action="uploadUrl"
                accept="image/*"
                :headers="headers"
                :show-file-list="false"
                :disabled="!uploadEnabled"
                :on-success="(res) => onHandleUploadSuccess(res, ['input_box', 'send_button_icon_url'])"
                ref="sendButtonIconUploader")
                aiq-button.upload-btn(size="small" :disabled="!uploadEnabled") {{ $t(`global.upload`) }}
              aiq-button.delete-btn(
                v-if="localWebchatUiCustomConfigs.input_box.send_button_icon_url"
                size="small"
                type="danger"
                @click="cleanSendButtonIcon") {{ $t(`global.delete`) }}
    .settings-container
      .settings__item
        .save-buttons__container.settings__item
          aiq-button(:disabled="isWebchatUiCustomConfigurationPristine" @click="resetWebchatUiCustomConfiguration") {{ $t(`global.cancel`) }}
          aiq-button.save__button(type="primary" :disabled="isWebchatUiCustomConfigurationPristine" @click="saveWebchatUiCustomConfiguration") {{ $t(`global.save`) }}
</template>

<script>
import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import { hosts } from '@/config/api.routes';

const DEFAULT_INPUT_BOX = () => ({
  branding_text_langkey: 'webchat.inputbox.branding_text',
  placeholder_text_langkey: 'webchat.inputbox.placeholder_text',
  text_limit: 999,
  upload_button_icon_url: '',
  send_button_icon_url: '',
});

const WEBCHAT_UI_CUSTOM_CONFIG_STRING_LANG_KEY_MAP = {
  'input_box.branding_text': 'input_box.branding_text_langkey',
  'input_box.placeholder_text': 'input_box.placeholder_text_langkey',
};

export default {
  name: 'webchat-ui-custom-configuration',
  props: {
    configs: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['save'],
  data() {
    return {
      BASE_TRANSLATION_PATH: 'settings_tab.channels_tab.webchat_ui.webchat_ui_custom_configuration',
      localWebchatUiCustomConfigs: this.cloneLocalWebchatUiCustomConfigs(this.configs),
      localWebchatUiCustomConfigStrings: this.cloneLocalWebchatUiCustomConfigStrings(
        this.webchatLanguageStrings || {},
        this.configs,
      ),
      uploadUrl: `${hosts.management}files/upload`,
      uploadEnabled: true,
      headers: {
        Authorization: this.$store.getters['agent/authToken'],
      },
    };
  },
  computed: {
    ...mapState({
      webchatLanguageStrings: state => state.configs.language_strings,
    }),
    isWebchatUiCustomConfigurationPristine() {
      const baselineStrings = this.cloneLocalWebchatUiCustomConfigStrings(
        this.webchatLanguageStrings || {},
        this.configs,
      );

      return isEqual(
        get(this.localWebchatUiCustomConfigs, 'input_box', {}),
        get(this.configs, 'input_box', {}),
      ) && isEqual(this.localWebchatUiCustomConfigStrings, baselineStrings);
    },
  },
  watch: {
    configs(newVal) {
      this.localWebchatUiCustomConfigs = this.cloneLocalWebchatUiCustomConfigs(newVal);
      this.localWebchatUiCustomConfigStrings = this.cloneLocalWebchatUiCustomConfigStrings(
        this.webchatLanguageStrings || {},
        newVal,
      );
    },
    webchatLanguageStrings: {
      handler(newVal) {
        this.localWebchatUiCustomConfigStrings = this.cloneLocalWebchatUiCustomConfigStrings(
          newVal || {},
          this.configs,
        );
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    cloneLocalWebchatUiCustomConfigs(newConfig) {
      const cfg = cloneDeep(newConfig || {});
      set(cfg, 'input_box', { ...DEFAULT_INPUT_BOX(), ...get(cfg, 'input_box', {}) });
      return cfg;
    },
    cloneLocalWebchatUiCustomConfigStrings(languageStringsRoot, configs) {
      const sourceConfigs = configs || this.localWebchatUiCustomConfigs;
      const cfgStrings = {};
      Object.keys(WEBCHAT_UI_CUSTOM_CONFIG_STRING_LANG_KEY_MAP).forEach(key => {
        const langKeyPath = WEBCHAT_UI_CUSTOM_CONFIG_STRING_LANG_KEY_MAP[key];
        const pathInLocaleFile = get(sourceConfigs, langKeyPath, '');
        const localeString = typeof pathInLocaleFile === 'string' && pathInLocaleFile.length > 0
          ? get(languageStringsRoot, pathInLocaleFile, '')
          : '';
        set(cfgStrings, key, localeString);
      });
      return cfgStrings;
    },
    buildWebchatUiCustomConfigPayload() {
      return Object.keys(WEBCHAT_UI_CUSTOM_CONFIG_STRING_LANG_KEY_MAP).reduce((acc, key) => {
        const langKeyPath = get(
          this.localWebchatUiCustomConfigs,
          WEBCHAT_UI_CUSTOM_CONFIG_STRING_LANG_KEY_MAP[key],
          '',
        );
        if (!langKeyPath) {
          return acc;
        }
        set(acc, langKeyPath, get(this.localWebchatUiCustomConfigStrings, key, ''));
        return acc;
      }, {});
    },
    upload() {
      return async(data) => {
        try {
          return await this.$store.dispatch('files/uploadPublicManagementFile', { file: data.file });
        } catch (err) {
          this.onUploadFailed(get(err, 'response.data.error'));
        }
      };
    },
    onHandleUploadSuccess(res, urlPath) {
      if (!res?.url) {
        return this.onUploadFailed();
      }
      set(this.localWebchatUiCustomConfigs, urlPath, res.url);
      this.resetUploading();
      this.$aiq.notify.success('Successful');
    },
    onUploadFailed(errorMsg = 'Failed') {
      this.$aiq.notify.error(errorMsg);
      this.resetUploading();
      this.$refs.uploadButtonIconUploader && this.$refs.uploadButtonIconUploader.clearFiles();
      this.$refs.sendButtonIconUploader && this.$refs.sendButtonIconUploader.clearFiles();
    },
    resetUploading() {
      this.uploadEnabled = true;
    },
    cleanUploadButtonIcon() {
      this.localWebchatUiCustomConfigs.input_box.upload_button_icon_url = '';
    },
    cleanSendButtonIcon() {
      this.localWebchatUiCustomConfigs.input_box.send_button_icon_url = '';
    },
    resetWebchatUiCustomConfiguration() {
      this.localWebchatUiCustomConfigs = this.cloneLocalWebchatUiCustomConfigs(this.configs);
      this.localWebchatUiCustomConfigStrings = this.cloneLocalWebchatUiCustomConfigStrings(
        this.webchatLanguageStrings || {},
        this.configs,
      );
    },
    saveWebchatUiCustomConfiguration() {

      // Validate text limit
      if (!Number.isFinite(this.localWebchatUiCustomConfigs.input_box.text_limit)
        || this.localWebchatUiCustomConfigs.input_box.text_limit < 0) {
        this.localWebchatUiCustomConfigs.input_box.text_limit = DEFAULT_INPUT_BOX().text_limit;
      }

      this.$emit('save', {
        webchatUiCustomConfig: this.localWebchatUiCustomConfigs,
        configStrings: this.buildWebchatUiCustomConfigPayload(),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../styles/aiq-variables.scss";

.webchat-ui-custom-configuration {
  * {
    margin: 5px 0px;
  }

  .no-margin {
    margin: 0px;
    padding-right: 10px;
  }

  .settings-container {
    display: flex;

    .settings__item {
      align-self: flex-start;
      flex-grow: 1;
      min-width: 589px;
    }
  }

  .header-config-container {
    width: 482px;

    .field-label {
      color: #475669;
      font-family: $aiq-font-family;
      font-size: 14px;
      font-weight: 300;
      letter-spacing: 0;
    }

    .sub-field {
      padding-left: 14px;
    }
  }

  .inline-label-box {
    display: flex;
  }

  .upload-button {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .save-buttons__container {
    background-color: var(--aiq-color-white);
    bottom: 0;
    display: flex;
    justify-content: flex-end;
    left: 0;
    margin-bottom: 0px;
    padding: 5px 8px 0px 0px;
    width: calc(100% - 4px);

    * {
      margin: 0px 5px;
    }

    .save__button {
      margin-right: 0px;
    }
  }

  .icon-preview {
    max-height: 40px;
    max-width: 40px;
  }
}
</style>
